// =============================================
// GASTOS DE LA VILLA - PROPIETARIO
// Transparencia de gastos comunes
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PieChart as ChartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ownerPortalService from '../../services/ownerPortalService';
import api from '../../services/api';

const OwnerExpenses = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState('');
  const [searchYearsBack, setSearchYearsBack] = useState(3); // Parámetro de años hacia atrás

  useEffect(() => {
    loadSystemParameters();
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [year, month]);

  const loadSystemParameters = async () => {
    try {
      const response = await api.get('/system-parameters');
      const params = response.data.parameters || [];
      const searchYearsParam = params.find(p => p.param_key === 'search_years_back');
      
      if (searchYearsParam) {
        setSearchYearsBack(parseInt(searchYearsParam.param_value) || 3);
      }
    } catch (err) {
      console.error('Error al cargar parámetros del sistema:', err);
      // Mantener el valor por defecto de 3
    }
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const filters = { year };
      if (month) filters.month = month;
      
      const response = await ownerPortalService.getExpenses(filters);
      setExpenses(response.expenses || []);
      setByCategory(response.byCategory || []);
      setTotal(response.total || 0);
      setError('');
    } catch (err) {
      setError('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const getMonthName = (month) => {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months[month - 1];
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: searchYearsBack }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/owner/dashboard')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4">
          Gastos de la Villa
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Transparencia total sobre en qué se invierten las cuotas mensuales
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Año</InputLabel>
              <Select
                value={year}
                label="Año"
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select
                value={month}
                label="Mes"
                onChange={(e) => setMonth(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {months.map((m) => (
                  <MenuItem key={m} value={m}>{getMonthName(m)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
              <Typography variant="body2" color="white">Total Gastado</Typography>
              <Typography variant="h6" color="white" fontWeight="bold">
                {formatCurrency(total)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Resumen por Categoría */}
      {byCategory.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChartIcon /> Distribución por Categoría
          </Typography>
          <Grid container spacing={2}>
            {byCategory.map((cat, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {cat.category}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {formatCurrency(cat.total)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {cat.count} {cat.count === 1 ? 'gasto' : 'gastos'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Tabla de Gastos */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Monto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map((expense) => (
                <TableRow key={expense.id} hover>
                  <TableCell>{formatDate(expense.expense_date)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={expense.category || 'Sin categoría'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {expense.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="error.main">
                      {formatCurrency(expense.amount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}

              {/* Fila de total */}
              {expenses.length > 0 && (
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell colSpan={3} align="right">
                    <Typography fontWeight="bold">
                      TOTAL ({expenses.length} gastos):
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="error.main" variant="h6">
                      {formatCurrency(total)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {expenses.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No hay gastos registrados para el período seleccionado
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}
    </Container>
  );
};

export default OwnerExpenses;
